// eslint import/no-named-as-default-member
import React from 'react';
import PropTypes from 'prop-types';
import './_page.scss';
import { withCookies } from 'react-cookie';
import { useLocation } from 'react-router';
import { sentryException } from '/01_fundaments/utilities/api';
import { handleUrlParameters } from '/01_fundaments/utilities/urlParameters';
import { errorMessages } from '/01_fundaments/utilities/errorMessages';
import ErrorBoundary from '/01_fundaments/utilities/ErrorBoundary';
import { AppContext } from '/00_global/scripts/App';
import { SEARCH } from '/01_fundaments/utilities/searchParameterNames';
import { LINK_CONTACT_US, LINK_FAQ_ERRORS } from '/01_fundaments/utilities/links';
import ebaBrandMapping from './ebaBrandMapping.json';

import {
	ACHTERNAAM,
	BIRTHDATE,
	CSRF_COOKIE_NAME,
	EMAIL,
	GENDER,
	INTERACTION,
	LAND,
	LOGIN_CODE,
	LOGIN_HINT,
	PHONE,
	PICTURE,
	POSTCODE,
	STRAAT,
	VOORNAAM,
	VOORNAAM_OUDER,
	WOONPLAATS,
} from '/01_fundaments/utilities/fieldNames';

import * as PATHS from '/00_global/scripts/paths';
import { EBA_PAGE_IDS } from '/00_global/scripts/paths';
import Placeholder from '/05_buckets/Placeholder';
import { socialLogin } from '/01_fundaments/utilities/account';
import { Page } from '@user-services/web-components';

const { PAGE_TITLES } = PATHS;
/*=============================================
= START - Placeholders
=============================================*/

// placeholders are lightweight and allow React to show
// a 'skeleton' while react fetches the new code from the server.

/*=============================================
= END - Placeholders
=============================================*/


/*=============================================
= START - Components
=============================================*/

// React code splitting allows for fast load times of a SPA.
// If you don't use this, loading would take forever !!!
// https://reactjs.org/docs/code-splitting.html

// To achieve this we use React.lazy, to dynamically fetch code when it is needed.
// https://reactjs.org/docs/code-splitting.html#reactlazy
const pages = {
	[PATHS.PATH_HOME]: React.lazy(() => import('/05_buckets/Pages/Aanmelden')),
	[PATHS.PATH_REGISTRATION]: React.lazy(() => import('/05_buckets/Pages/Registratie')),
	[PATHS.PATH_REGISTRATION_DETAILS]: React.lazy(() => import('/05_buckets/Pages/RegistratieDetails')),
	[PATHS.PATH_REGISTRATION_COMMERCIAL]: React.lazy(() => import('/05_buckets/Pages/RegistratieCommercial')),
	[PATHS.PATH_REGISTRATION_PROVIDER]: React.lazy(() => import('/05_buckets/Pages/RegistratieProvider')),
	[PATHS.PATH_REGISTRATION_CONSENTS]: React.lazy(() => import('/05_buckets/Pages/RegistratieConsents')),
	[PATHS.PATH_REGISTRATION_CONFIRMATION]: React.lazy(() => import('/05_buckets/Pages/RegistratieBevestigen')),
	[PATHS.PATH_REGISTRATION_DONE]: React.lazy(() => import('/05_buckets/Pages/RegistratieAfronden')),
	[PATHS.PATH_SUBPROFILE_REGISTRATION]: React.lazy(() => import('/05_buckets/Pages/KinderprofielRegistratie')),
	[PATHS.PATH_SUBPROFILE_LOGIN]: React.lazy(() => import('/05_buckets/Pages/KinderprofielAanmelden')),
	[PATHS.PATH_SUBPROFILE_SELECTION]: React.lazy(() => import('/05_buckets/Pages/KinderprofielSelectie')),
	[PATHS.PATH_SUBPROFILE_REGISTRATION_DETAILS]: React.lazy(() => import('/05_buckets/Pages/KinderprofielRegistratieDetails')),
	[PATHS.PATH_SUBPROFILE_LOGIN_VERIFICATION]: React.lazy(() => import('/05_buckets/Pages/KinderprofielVerificatie')),
	[PATHS.PATH_SUBPROFILE_LOGGED_IN_CHILD]: React.lazy(() => import('/05_buckets/Pages/KinderprofielAangemeldKind')),
	[PATHS.PATH_SUBPROFILE_LOGGED_IN_PARENT]: React.lazy(() => import('/05_buckets/Pages/KinderprofielAangemeldOuder')),
	[PATHS.PATH_FORGOT_PASSWORD]: React.lazy(() => import('/05_buckets/Pages/WachtwoordVergeten')),
	[PATHS.PATH_FORGOT_PASSWORD_PENDING]: React.lazy(() => import('/05_buckets/Pages/WachtwoordAanvraag')),
	[PATHS.PATH_RESET_PASSWORD]: React.lazy(() => import('/05_buckets/Pages/WachtwoordWijzigen')),
	[PATHS.PATH_RESET_PASSWORD_DONE]: React.lazy(() => import('/05_buckets/Pages/WachtwoordAangepast')),
	[PATHS.PATH_LINK_ACCOUNTS]: React.lazy(() => import('/05_buckets/Pages/LinkAccounts')),
	[PATHS.PATH_SOCIAL_EMAIL]: React.lazy(() => import('/05_buckets/Pages/LinkEmail')),
	[PATHS.PATH_PORTABILITY]: React.lazy(() => import('/05_buckets/Pages/Portabiliteit')),
	[PATHS.PATH_TV_VERIFICATION]: React.lazy(() => import('/05_buckets/Pages/TVVerificatie')),
	[PATHS.PATH_TV_LINKED]: React.lazy(() => import('/05_buckets/Pages/TVGekoppeld')),
	[PATHS.PATH_NO_MATCH]: React.lazy(() => import('/05_buckets/Pages/404')),
};

/*=============================================
= END - Components
=============================================*/


/*=============================================
= START - Page context
=============================================*/

// This creates a 'container' for data to pass to child components
// https://reactjs.org/docs/context.html#reactcreatecontext

export const PageContext = React.createContext();

/*=============================================
= END - App context
=============================================*/

PageTemplate.propTypes = {
	match: PropTypes.any,
	cookies: PropTypes.any,
	location: PropTypes.object,
}

function PageTemplate(props) {
	/*--------------------------------------------
	- General setup
	---------------------------------------------*/

	// router path match
	const path = useLocation().pathname;

	// use context App
	// https://reactjs.org/docs/hooks-reference.html#usecontext
	const { showNotification, analyticsLoaded } = React.useContext(AppContext);


	// cookie
	const csrfToken = props.cookies.get(CSRF_COOKIE_NAME) || '';


	// url params
	const params = handleUrlParameters(window.location);


	/*=============================================
	= START - pagePath setup
	=============================================*/

	// As long as backendData.pagePath is not false,
	// use it as the initialPagePath, otherwise use
	// the path passed in as a prop.
	// The reason for this is that the backend controls the
	// initial page path to render. Then the app takes over.
	const initialPagePath = window.backendData.pagePath || path;

	// On initial render we define the originalPagePath for later use in child components (flow logic related).
	// We set backendData.pagePath to false, so the app will follow the path given by React Router when we
	// start navigation through the pages.
	// https://reactjs.org/docs/hooks-reference.html#useeffect
	React.useEffect(() => {
		window.backendData.originalPagePath = window.backendData.pagePath;
		window.backendData.pagePath = false;
	}, []);

	// This logic allows Child components to request a new page path with 'handlePageChange'.
	// Once the unmount animation finishes for the active page, the 'pagePathToRender' is set
	// to the new requested path, and the app loads the new page.
	const [pagePathToRender, setPagePathToRender] = React.useState(initialPagePath || initialPagePath);

	function handlePageChange(nextPage) {
		// setAnimationType('unmount');
		setPagePathToRender(nextPage);
	}

	/*=============================================
	= END - pagePath setup
	=============================================*/


	/*=============================================
	= START - Cookies disabled
	=============================================*/

	// Cookies disabled
	React.useEffect(() => {

		// if csrfToken is an empty string, this possibly means
		// the user has disabled cookies in his/her browser.

		// The switch statement checks the 'pagePathToRender'
		// and shows a notification if the page has a dependency on it.

		// The last argument for 'showNotification' is set to 'true',
		// so the message stays on screen (no auto close).

		// development
		if (import.meta.env.DEV) {
			return;
		}

		// production
		if (!csrfToken) {
			switch (pagePathToRender) {
				case PATHS.PATH_REGISTRATION_DONE:
				case PATHS.PATH_RESET_PASSWORD:
				case PATHS.PATH_RESET_PASSWORD_DONE:
					break;
				default:
					showNotification(
						'error',
						`Het VRT-profiel vereist dat je browser cookies aanvaardt.
						Controleer de instellingen van jouw browser. <a href="${LINK_FAQ_ERRORS}" target="_blank" rel="noopener noreferrer">Meer info</a>`,
						true
					);
					break;
			}
		}

		// eslint-disable-next-line
	}, [csrfToken, pagePathToRender]);

	/*=============================================
	= END - Cookies disabled
	=============================================*/

	/*=============================================
	= START - Add cancel login params
	=============================================*/

	// This function adds the parameters to the given url which are needed to cancel the login/registration process.
	// There is an assumption that the 'client_id' param is already present in the url. This param is always present anyway.

	function addCancelLoginParams(url) {
		// add redirect param
		if (params && Object.prototype.hasOwnProperty.call(params, SEARCH.REDIRECT_URI)) {
			url += `${getQueryDelimiter(url)}${SEARCH.REDIRECT_URI}=${encodeURIComponent(params[SEARCH.REDIRECT_URI])}`;
		}

		// add state param
		if (params && Object.prototype.hasOwnProperty.call(params, SEARCH.STATE)) {
			url += `${getQueryDelimiter(url)}${SEARCH.STATE}=${encodeURIComponent(params[SEARCH.STATE])}`;
		}

		return url;
	}

	function getQueryDelimiter(url) {
		return url.match(/[?]/g) ? '&' : '?';
	}

	function getRedirectUriParam() {
		if (params && Object.prototype.hasOwnProperty.call(params, SEARCH.REDIRECT_URI)) {
			return params[SEARCH.REDIRECT_URI];
		}
	}

	function getStateParam() {
		if (params && Object.prototype.hasOwnProperty.call(params, SEARCH.STATE)) {
			return params[SEARCH.STATE];
		}
	}

	function getEbaTouchpointBrand(clientId) {
		if (!clientId) return 'vrt';
		if (clientId.endsWith("-dev") || clientId.endsWith("-stag")) {
			clientId = removeEnvironmentSuffixFromClientId(clientId);
		}
		return ebaBrandMapping[clientId] ? ebaBrandMapping[clientId] : 'vrt';
	}

	function removeEnvironmentSuffixFromClientId(clientId) {
		return clientId.slice(0, clientId.lastIndexOf("-"));
	}

	/*=============================================
	= END - Add cancel login params
	=============================================*/


	/*=============================================
	= START - Event based analytics
	=============================================*/
	const [previousPagePath, setPreviousPagePath] = React.useState('');

	window.VRT = window.VRT || {};
	window.VRT.UserServices = window.VRT.UserServices || {}

	React.useEffect(() => {
		if (pagePathToRender !== previousPagePath) { // to prevent firing page impressions multiple times
			setPreviousPagePath(pagePathToRender);

			// variables
			const title = 'VRT-profiel | ' + PAGE_TITLES[pagePathToRender];
			const ebaPageId = EBA_PAGE_IDS[pagePathToRender] || 'profiel-onbekend';
			const ebaTouchpointBrand = getEbaTouchpointBrand(window.backendData.clientId);

			// set page title
			window.document.title = title;

			// set Analytics data
			window.VRT.UserServices.getCorrelationId = () => {
				return subProfileLoginData[LOGIN_CODE];
			};
			try {
				// emit page impression event with context
				window.digitalData.events.push({
					event: {
						schema: "iglu:be.vrt/page_impression/jsonschema/1-1-0",
						data: {
							timestamp: Date.now()
						}
					},
					context: [
						{
							schema: "iglu:be.vrt/context_page/jsonschema/1-2-3",
							data: {
								pageid: ebaPageId,
								pagetitle: title,
								pagename: pagePathToRender,
								touchpointbrand: ebaTouchpointBrand,
								contentbrand: 'vrt',
								sitesection1: 'onboarding',
								sitesection2: 'profile',
							}
						}, {
							schema: "iglu:be.vrt/context_platform/jsonschema/1-2-2",
							data: {
								platform: 'web',
								environment: import.meta.env.PROD ? "prod" : "stag",
								touchpointbrand: ebaTouchpointBrand,
							},
						},
					]
				});
			} catch (e) {
				// eslint-disable-next-line no-console
				console.log('unable to send analytics :', e.message);
			}
		}
	}, [pagePathToRender]);

	/*=============================================
	= END - Event based analytics
	=============================================*/


	/*=============================================
	= START - User & subprofile login state
	=============================================*/

	// Formatting the user & subprofile login data makes sure the app always gets a consistent data object
	// with consistent key:value pairs

	let initialUserData = window.backendData.user ? {
		[VOORNAAM]: window.backendData.user[VOORNAAM] || undefined,
		[ACHTERNAAM]: window.backendData.user[ACHTERNAAM] || undefined,
		[BIRTHDATE]: window.backendData.user[BIRTHDATE] || undefined,
		[GENDER]: window.backendData.user[GENDER] || undefined,
		[EMAIL]: window.backendData[EMAIL] || undefined,
		[PHONE]: window.backendData.user[PHONE] || undefined,
		[STRAAT]: window.backendData.user[STRAAT] || undefined,
		[WOONPLAATS]: window.backendData.user[WOONPLAATS] || undefined,
		[POSTCODE]: window.backendData.user[POSTCODE] || undefined,
		[LAND]: window.backendData.user[LAND] || undefined,
	} : {
		[EMAIL]: window.backendData[EMAIL] || undefined,
	};

	let initialSubProfileLoginData = window.backendData.subProfileLogin ? {
		[LOGIN_CODE]: window.backendData.subProfileLogin[LOGIN_CODE] || undefined,
		[PICTURE]: window.backendData.subProfileLogin[PICTURE] || undefined,
		[VOORNAAM]: window.backendData.subProfileLogin[VOORNAAM] || undefined,
		[BIRTHDATE]: window.backendData.subProfileLogin[BIRTHDATE] || undefined,
		[INTERACTION]: window.backendData.subProfileLogin[INTERACTION] || false,
		[VOORNAAM_OUDER]: window.backendData.subProfileLogin[VOORNAAM_OUDER] || undefined,
	} : {};

	// store initial user & subprofile data
	const [userData, setUserData] = React.useState(initialUserData);
	const [subProfileLoginData, setSubProfileLoginData] = React.useState(initialSubProfileLoginData);

	function handleUpdateUserData(dataObject) {
		const newUserData = {
			...userData,
			...dataObject
		};
		setUserData(newUserData);
	}

	function handleUpdateSubProfileLoginData(dataObject) {
		const newSubProfileLoginData = {
			...subProfileLoginData,
			...dataObject
		};
		setSubProfileLoginData(newSubProfileLoginData);
	}

	/*=============================================
	= END - User & subprofile login state
	=============================================*/


	/*=============================================
	= START - handle Gigya error codes
	=============================================*/

	// if the backend encounters a gigya error,
	// it can add an errorCode to the backendData and
	// this logic will show the error to the user.

	if (window.backendData.errorCode > 0) {
		if (errorMessages[window.backendData.errorCode]) {
			showNotification('error', errorMessages[window.backendData.errorCode].notification);
		} else if (window.backendData.errorCode !== 200009) {
			showNotification('error', `Er liep iets mis. Indien dit probleem aanhoudt, <a href="${LINK_CONTACT_US}" target="_blank" rel="noopener noreferrer">contacteer de klantendienst</a>.`);
			const err = new Error(window.backendData);
			err.name = `Gigya Error ${window.backendData.errorCode} on backend`;
			sentryException(err);
		}

		// clear errorCode to prevent multiple notifications
		window.backendData.originalErrorCode = window.backendData.errorCode;
		window.backendData.errorCode = null;
	}

	/*=============================================
	= END - handle Gigya error codes
	=============================================*/


	/*=============================================
	= START - Handle loginHint
	=============================================*/

	// The backend can pass a loginHint (Social login provider).
	// This will call gigya.accounts.socialLogin() directly
	// without rendering the app itself.

	const hasLoginHint = (window.backendData[LOGIN_HINT] === 'googleplus' || window.backendData[LOGIN_HINT] === 'facebook' || window.backendData[LOGIN_HINT] === 'apple') || false;

	React.useEffect(() => {
		if (hasLoginHint) {
			socialLogin(csrfToken, window.backendData[LOGIN_HINT], getRedirectUriParam(), getStateParam())
				.then((response) => {
					window.location = response.redirectUrl;
				})
		}

		// eslint-disable-next-line
	}, [hasLoginHint, csrfToken]);

	/*=============================================
	= END - Handle loginHint
	=============================================*/

	/*=============================================
	= START - Scroll handler
	=============================================*/

	// Scroll to page top, if page path changes
	// In a SPA context, changing pages does not scroll the page back to the top
	// like the browser does by default.

	React.useEffect(() => {

		// disable default scroll restoration
		if (
			'scrollRestoration' in window.history &&
			window.history.scrollRestoration !== 'manual'
		) {
			window.history.scrollRestoration = 'manual';
		}

		// scroll to top of page
		try {
			window.scrollTo({
				top: 0,
				left: 0,
				behavior: 'auto'
			})
		} catch (error) {
			// eslint-disable-next-line no-console
			console.log(error);
		}

	}, [pagePathToRender]);

	/*=============================================
	= END - Scroll handler
	=============================================*/


	/*=============================================
	= START - PageContext
	=============================================*/

	// Here we set all the data, functions, etc.
	// that we would like to make available for child components.
	const pageContextValue = {
		handlePageChange,
		userData,
		handleUpdateUserData,
		subProfileLoginData,
		handleUpdateSubProfileLoginData,
		params,
		csrfToken,
		addCancelLoginParams,
		getRedirectUriParam,
		getStateParam,
	};

	/*=============================================
	= END - AppContext
	=============================================*/


	/* don't render anything if loginHint provided
	---------------------------------------------*/
	if (hasLoginHint) {
		return (
			<div className="loadingContainer">
				<div className="initialLoader" aria-label="Even geduld aub. Pagina wordt geladen.">
					<div className="loadingContainer">
						<div className="loader"></div>
					</div>
				</div>
			</div>
		);
	}


	/* App can render
	---------------------------------------------*/

	// ErrorBoundary: makes sure the app doesn't crash if dynamic fetching of new code fails
	// https://reactjs.org/docs/error-boundaries.html

	// React.Suspense: allows us to render a placeholder component, until the real component gets rendered.
	// https://reactjs.org/docs/react-api.html#reactsuspense
	const ComponentToRender = Object.entries(pages).find(([key]) => key === pagePathToRender)[1];

	return (
		<Page title={pagePathToRender}>
			<PageContext.Provider value={pageContextValue}>
				<ErrorBoundary>
					<React.Suspense fallback={<Placeholder />}>
						<ComponentToRender handlePageChange={handlePageChange} />
					</React.Suspense>
				</ErrorBoundary>
			</PageContext.Provider>
		</Page>
	)
}

export default withCookies(PageTemplate);
